import React, { useEffect, useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, CalendarClock, CheckCircle2, Mail, PenLine, Plus, Save, Send, ShieldCheck, ToggleLeft, ToggleRight } from 'lucide-react';
import { createJourneyStep, generateJourneyHeldQueue, getEmailTemplates, getJourneySteps, getRsvpPendingAudience, reorderJourneySteps, updateJourneyStep } from '../../../data/api';
import { toast } from 'sonner';

const DEFAULT_STEPS = [
  { name: 'Save the Date', step_code: 'save_the_date', offset_value: 24, offset_unit: 'weeks', template_type: 'save_the_date' },
  { name: 'Official Invitation', step_code: 'official_invitation', offset_value: 12, offset_unit: 'weeks', template_type: 'invitation' },
  { name: 'Reminder 1', step_code: 'reminder_1', offset_value: 6, offset_unit: 'weeks', template_type: 'reminder' },
  { name: 'Reminder 2', step_code: 'reminder_2', offset_value: 3, offset_unit: 'weeks', template_type: 'reminder' },
  { name: 'Final Reminder', step_code: 'final_reminder', offset_value: 10, offset_unit: 'days', template_type: 'reminder' },
  { name: 'Thank You', step_code: 'thank_you', offset_value: 1, offset_unit: 'weeks', template_type: 'thank_you' },
];

function stepId(step) {
  return step?.id || step?._id || '';
}

function isEnabled(step) {
  return step.status !== 'disabled';
}

function timingLabel(step) {
  if (step.timing_mode === 'fixed_datetime' && step.fixed_at) {
    const date = new Date(step.fixed_at);
    return Number.isNaN(date.getTime()) ? step.fixed_at : date.toLocaleString();
  }
  if (step.offset_value && step.offset_unit) {
    return `${step.offset_value} ${step.offset_unit} before event`;
  }
  return 'Timing not set';
}

function templateName(templates, templateId) {
  const template = templates.find(item => (item.id || item._id) === templateId);
  return template?.name || 'No template assigned';
}

function milestoneState(step) {
  if (!isEnabled(step)) return { key: 'disabled', label: 'Disabled' };
  if (!step.template_id) return { key: 'needs-template', label: 'Needs Template' };
  return { key: 'ready', label: 'Ready' };
}

export default function JourneyPanel({ onPrepareSend = null, onOpenOutbox = null }) {
  const [steps, setSteps] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState('');
  const [queueingId, setQueueingId] = useState('');
  const [modifyMode, setModifyMode] = useState(false);

  const operationalTemplates = useMemo(
    () => templates.filter(template => template.template_type !== 'confirmation' && !template.is_system_protected && !template.is_live),
    [templates]
  );
  const sortedSteps = useMemo(
    () => [...steps].sort((a, b) => (a.order || 0) - (b.order || 0)),
    [steps]
  );
  const missingDefaultSteps = useMemo(() => {
    const configuredCodes = new Set(sortedSteps.map(step => String(step.step_code || '').toLowerCase()).filter(Boolean));
    const configuredNames = new Set(sortedSteps.map(step => String(step.name || '').toLowerCase()).filter(Boolean));
    return DEFAULT_STEPS.filter(preset => (
      !configuredCodes.has(preset.step_code.toLowerCase()) &&
      !configuredNames.has(preset.name.toLowerCase())
    ));
  }, [sortedSteps]);
  const completionAverage = useMemo(() => {
    if (!sortedSteps.length) return 0;
    const total = sortedSteps.reduce((sum, step) => sum + (Number(step.completion_rate) || 0), 0);
    return Math.round(total / sortedSteps.length);
  }, [sortedSteps]);

  const load = async () => {
    setLoading(true);
    try {
      const [journeyData, templateData] = await Promise.all([
        getJourneySteps(),
        getEmailTemplates(),
      ]);
      setSteps(Array.isArray(journeyData) ? journeyData : []);
      setTemplates(Array.isArray(templateData) ? templateData : []);
    } catch (error) {
      console.error(error);
      toast.error('Journey could not be loaded');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initializeJourney = async () => {
    const presets = sortedSteps.length ? missingDefaultSteps : DEFAULT_STEPS;
    if (!presets.length) return;
    setSavingId('initialize');
    try {
      const startingOrder = sortedSteps.reduce((max, step) => Math.max(max, Number(step.order) || 0), 0);
      for (let index = 0; index < presets.length; index += 1) {
        const preset = presets[index];
        const template = operationalTemplates.find(item => {
          const type = String(item.template_type || '').toLowerCase();
          const name = String(item.name || '').toLowerCase();
          return type === preset.template_type || name.includes(preset.name.toLowerCase().split(' ')[0]);
        });
        await createJourneyStep({
          name: preset.name,
          step_code: preset.step_code,
          channel: 'email',
          template_id: template ? (template.id || template._id) : null,
          timing_mode: 'offset',
          offset_value: preset.offset_value,
          offset_unit: preset.offset_unit,
          status: 'pending',
          order: startingOrder + index + 1,
        });
      }
      await load();
      setModifyMode(true);
      toast.success(sortedSteps.length ? 'Missing journey milestones added' : 'Recommended journey initialized');
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || 'Journey could not be initialized');
    } finally {
      setSavingId('');
    }
  };

  const updateLocalStep = (id, patch) => {
    setSteps(prev => prev.map(step => stepId(step) === id ? { ...step, ...patch } : step));
  };

  const saveStep = async (step) => {
    const id = stepId(step);
    setSavingId(id);
    try {
      const payload = {
        name: step.name,
        channel: 'email',
        template_id: step.template_id || null,
        timing_mode: step.timing_mode || 'offset',
        fixed_at: step.timing_mode === 'fixed_datetime' ? step.fixed_at || null : null,
        offset_value: step.timing_mode === 'offset' ? Number(step.offset_value) || 1 : null,
        offset_unit: step.timing_mode === 'offset' ? step.offset_unit || 'days' : null,
        status: step.status || 'pending',
        order: step.order,
      };
      const result = await updateJourneyStep(id, payload);
      if (result?.step) {
        updateLocalStep(id, result.step);
      }
      toast.success('Journey step saved');
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || 'Journey step could not be saved');
    } finally {
      setSavingId('');
    }
  };

  const moveStep = async (id, direction) => {
    const next = [...sortedSteps];
    const index = next.findIndex(step => stepId(step) === id);
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (index < 0 || targetIndex < 0 || targetIndex >= next.length) return;
    const [moved] = next.splice(index, 1);
    next.splice(targetIndex, 0, moved);
    const reordered = next.map((step, orderIndex) => ({ ...step, order: orderIndex + 1 }));
    setSteps(reordered);
    try {
      await reorderJourneySteps(reordered.map(step => ({ id: stepId(step), order: step.order })));
    } catch (error) {
      console.error(error);
      toast.error('Journey order could not be saved');
      await load();
    }
  };

  const toggleEnabled = (step) => {
    const id = stepId(step);
    updateLocalStep(id, { status: isEnabled(step) ? 'disabled' : 'pending' });
  };

  const prepareInSend = (step) => {
    if (!onPrepareSend) return;
    onPrepareSend({
      type: 'journey_milestone',
      purpose: String(step.step_code || '').includes('thank') ? 'thank_you' : (String(step.step_code || '').includes('reminder') ? 'reminder' : 'invitation'),
      channel: step.channel || 'email',
      templateId: step.template_id || '',
      audienceType: 'rsvp_pending',
      campaignKey: `journey:${step.step_code || stepId(step)}`,
      journeyStepId: stepId(step),
    });
  };

  const generateHeldQueue = async (step) => {
    const id = stepId(step);
    if (!id || !step.template_id) {
      toast.error('Assign a template before generating a held queue');
      return;
    }
    setQueueingId(id);
    try {
      const channel = step.channel || 'email';
      const campaignKey = `journey:${step.step_code || id}`;
      const audience = await getRsvpPendingAudience({ channel, campaign_key: campaignKey });
      if (!audience.counts?.eligible) {
        toast.error('No RSVP-pending guests are eligible for this milestone');
        return;
      }
      const confirmed = window.confirm(`Generate a held ${channel} queue for ${audience.counts.eligible} RSVP-pending guest(s)? Nothing will send until Outbox approval.`);
      if (!confirmed) return;
      const result = await generateJourneyHeldQueue(id, {
        audience_type: 'rsvp_pending',
        audience_hash: audience.audience_hash,
        channel,
        correlation_id: `journey-${id}-${Date.now()}`,
      });
      toast.success(result.message || 'Held queue generated');
      if (onOpenOutbox) onOpenOutbox();
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || 'Held queue could not be generated');
    } finally {
      setQueueingId('');
    }
  };

  return (
    <div className="communications-panel communications-journey" data-testid="communications-journey-panel">
      <div className="communications-panel__toolbar communications-studio-toolbar">
        <div>
          <p className="communications-panel__eyebrow">Guest Journey</p>
          <h2>Journey</h2>
          <p>Plan and prepare the message milestones for your event.</p>
        </div>
        <div className="communications-toolbar-actions">
          {missingDefaultSteps.length > 0 && (
            <button type="button" className="communications-button communications-button--ghost" onClick={initializeJourney} disabled={savingId === 'initialize' || loading}>
              <Plus size={16} />
              {sortedSteps.length ? 'Add Missing Milestones' : 'Initialize Journey'}
            </button>
          )}
          <button type="button" className="communications-button communications-button--primary" onClick={() => setModifyMode(mode => !mode)}>
            <PenLine size={16} />
            {modifyMode ? 'View Journey' : 'Modify Journey'}
          </button>
        </div>
      </div>

      <div className="communications-journey-overview">
        <div className="communications-journey-metric">
          <span>Steps</span>
          <strong>{sortedSteps.length || DEFAULT_STEPS.length}</strong>
        </div>
        <div className="communications-journey-metric">
          <span>Active</span>
          <strong>{sortedSteps.filter(isEnabled).length}</strong>
        </div>
      </div>

      {loading ? (
        <div className="communications-empty"><CalendarClock size={24} /><p>Loading journey...</p></div>
      ) : !sortedSteps.length ? (
        <div className="communications-journey-empty">
          <CalendarClock size={32} />
          <h3>Recommended journey is ready to initialize</h3>
          <p>Create the six approved milestones, then assign templates and timing before any live use.</p>
          <button type="button" className="communications-button communications-button--primary" onClick={initializeJourney} disabled={savingId === 'initialize'}>
            <Plus size={16} />
            Initialize Recommended Journey
          </button>
        </div>
      ) : (
        <div className={`communications-journey-timeline ${modifyMode ? 'communications-journey-timeline--modify' : ''}`}>
	          {sortedSteps.map((step, index) => {
	            const id = stepId(step);
	            const state = milestoneState(step);
	            return (
	              <article key={id} className={`communications-journey-card communications-journey-card--${state.key} ${!isEnabled(step) ? 'communications-journey-card--disabled' : ''}`}>
	                <div className="communications-journey-card__rail">
	                  <span>{index + 1}</span>
	                </div>
	                <div className="communications-journey-card__main">
	                  <div className="communications-journey-card__title">
                    {modifyMode ? (
                      <input value={step.name || ''} onChange={event => updateLocalStep(id, { name: event.target.value })} />
                    ) : (
	                      <h3>{step.name}</h3>
	                    )}
	                    <span className={`communications-pill communications-pill--${state.key}`}>
	                      {state.label}
	                    </span>
	                  </div>

                  {modifyMode ? (
                    <div className="communications-journey-edit-grid">
                      <label>
                        <span>Template</span>
                        <select value={step.template_id || ''} onChange={event => updateLocalStep(id, { template_id: event.target.value })}>
                          <option value="">No template assigned</option>
                          {operationalTemplates.map(template => (
                            <option key={template.id || template._id} value={template.id || template._id}>{template.name}</option>
                          ))}
                        </select>
                      </label>
                      <label>
                        <span>Timing</span>
                        <select value={step.timing_mode || 'offset'} onChange={event => updateLocalStep(id, { timing_mode: event.target.value })}>
                          <option value="offset">Relative to event</option>
                          <option value="fixed_datetime">Fixed date/time</option>
                        </select>
                      </label>
                      {step.timing_mode === 'fixed_datetime' ? (
                        <label>
                          <span>Fixed At</span>
                          <input type="datetime-local" value={step.fixed_at || ''} onChange={event => updateLocalStep(id, { fixed_at: event.target.value })} />
                        </label>
                      ) : (
                        <>
                          <label>
                            <span>Offset</span>
                            <input type="number" min="1" value={step.offset_value || 1} onChange={event => updateLocalStep(id, { offset_value: event.target.value })} />
                          </label>
                          <label>
                            <span>Unit</span>
                            <select value={step.offset_unit || 'days'} onChange={event => updateLocalStep(id, { offset_unit: event.target.value })}>
                              <option value="days">Days</option>
                              <option value="weeks">Weeks</option>
                              <option value="hours">Hours</option>
                            </select>
                          </label>
                        </>
                      )}
                    </div>
                  ) : (
	                    <div className="communications-journey-card__meta">
	                      <span><Mail size={14} /> {templateName(operationalTemplates, step.template_id)}</span>
	                      <span className="communications-journey-timechip"><CalendarClock size={14} /> {timingLabel(step)}</span>
	                      <span title={`${step.completed_count || 0} of ${step.total_guests || 0} guests have received this milestone`}><CheckCircle2 size={14} /> {step.completed_count || 0} of {step.total_guests || 0} guests reached</span>
	                    </div>
                  )}
                </div>

                {modifyMode && (
                  <div className="communications-journey-card__actions">
                    <button
                      type="button"
                      onClick={() => moveStep(id, 'up')}
                      disabled={index === 0}
                      title="Move step up"
                      aria-label="Move step up"
                    >
                      <ArrowUp size={15} />
                    </button>
                    <button
                      type="button"
                      onClick={() => moveStep(id, 'down')}
                      disabled={index === sortedSteps.length - 1}
                      title="Move step down"
                      aria-label="Move step down"
                    >
                      <ArrowDown size={15} />
                    </button>
                    <button
                      type="button"
                      onClick={() => toggleEnabled(step)}
                      title={isEnabled(step) ? 'Disable step' : 'Enable step'}
                      aria-label={isEnabled(step) ? 'Disable step' : 'Enable step'}
                    >
                      {isEnabled(step) ? <ToggleRight size={17} /> : <ToggleLeft size={17} />}
                    </button>
                    <button type="button" className="communications-button communications-button--primary" onClick={() => saveStep(step)} disabled={savingId === id}>
                      <Save size={15} />
                      Save
                    </button>
                  </div>
                )}
                {!modifyMode && (
                  <div className="communications-journey-card__actions communications-journey-card__actions--view">
                    <button
                      type="button"
                      className="communications-button communications-button--primary"
                      onClick={() => prepareInSend(step)}
                      disabled={!step.template_id || !onPrepareSend}
                      title={!step.template_id ? 'Assign a template to this milestone first' : 'Prepare this milestone in the Send tab'}
                    >
                      <Send size={15} />
                      Prepare in Send
                    </button>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}

    </div>
  );
}
